/**
 * AsyncStorage stub for web
 * Provides localStorage-based implementation compatible with React Native AsyncStorage API
 */

declare const window: any;

class AsyncStorageWeb {
  private static PREFIX = '@AsyncStorage:';

  static setItem = async (key: string, value: string) => {
    try {
      if (typeof window !== 'undefined' && window.localStorage) {
        window.localStorage.setItem(AsyncStorageWeb.PREFIX + key, value);
      }
      return null;
    } catch (error) {
      return error;
    }
  };

  static getItem = async (key: string) => {
    try {
      if (typeof window !== 'undefined' && window.localStorage) {
        const value = window.localStorage.getItem(AsyncStorageWeb.PREFIX + key);
        return value || null;
      }
      return null;
    } catch (error) {
      return null;
    }
  };

  static removeItem = async (key: string) => {
    try {
      if (typeof window !== 'undefined' && window.localStorage) {
        window.localStorage.removeItem(AsyncStorageWeb.PREFIX + key);
      }
      return null;
    } catch (error) {
      return error;
    }
  };

  static multiSet = async (keyValuePairs: Array<[string, string]>) => {
    try {
      if (typeof window !== 'undefined' && window.localStorage) {
        for (const [key, value] of keyValuePairs) {
          window.localStorage.setItem(AsyncStorageWeb.PREFIX + key, value);
        }
      }
      return null;
    } catch (error) {
      return error;
    }
  };

  static multiGet = async (keys: string[]) => {
    try {
      if (typeof window !== 'undefined' && window.localStorage) {
        return keys.map((key) => [
          key,
          window.localStorage.getItem(AsyncStorageWeb.PREFIX + key),
        ]);
      }
      return [];
    } catch (error) {
      return [];
    }
  };

  static multiRemove = async (keys: string[]) => {
    try {
      if (typeof window !== 'undefined' && window.localStorage) {
        for (const key of keys) {
          window.localStorage.removeItem(AsyncStorageWeb.PREFIX + key);
        }
      }
      return null;
    } catch (error) {
      return error;
    }
  };

  static getAllKeys = async () => {
    try {
      if (typeof window !== 'undefined' && window.localStorage) {
        const keys: string[] = [];
        for (let i = 0; i < window.localStorage.length; i++) {
          const key = window.localStorage.key(i);
          if (key && key.startsWith(AsyncStorageWeb.PREFIX)) {
            keys.push(key.substring(AsyncStorageWeb.PREFIX.length));
          }
        }
        return keys;
      }
      return [];
    } catch (error) {
      return [];
    }
  };

  static clear = async () => {
    try {
      if (typeof window !== 'undefined' && window.localStorage) {
        const keysToRemove: string[] = [];
        for (let i = 0; i < window.localStorage.length; i++) {
          const key = window.localStorage.key(i);
          if (key && key.startsWith(AsyncStorageWeb.PREFIX)) {
            keysToRemove.push(key);
          }
        }
        for (const key of keysToRemove) {
          window.localStorage.removeItem(key);
        }
      }
      return null;
    } catch (error) {
      return error;
    }
  };
}

export default AsyncStorageWeb;
